import AuthLayout from "@/Frontend/Layouts/AuthLayout"
import { Link, useForm } from "@inertiajs/react"
import translate from "@/utils/translate"
import BioneMeta from "@/utils/BioneMeta"
import { useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import LoginLink from "@/Frontend/Components/LoginLink"

export default function Login({ is_facebook_login_active, is_demo, is_google_login_active, meta_tags, tagline, site_name, flash }) {
    const captchaSiteKey = localStorage.getItem("google_captcha_site_key") ? JSON.parse(localStorage.getItem("google_captcha_site_key")) : []
    const is_active_google_captcha = localStorage.getItem("is_active_google_captcha")
        ? JSON.parse(localStorage.getItem("is_active_google_captcha"))
        : []
    const [captchaVerified, setCaptchaVerified] = useState(false)
    const [captchaError, setCaptchaError] = useState(null)

    const { data, errors, post, setData, processing } = useForm({
        login: "",
        password: "",
        remember: false
    })

    BioneMeta(tagline, "", meta_tags, "", "", site_name)

    const handleCaptchaChange = (value) => {
        if (is_active_google_captcha === "1") {
            if (value) {
                setData("captchaToken", value)
                setCaptchaVerified(true)
                setCaptchaError(null)
            } else {
                setCaptchaVerified(false)
            }
        }
    }

    // Toggle password visibility
    const togglePasswordVisibility = (id) => {
        const input = document.getElementById(id);
        if (input) {
            input.type = input.type === 'password' ? 'text' : 'password';
        }
    }

    // handle login
    const handleLogin = (e) => {
        e.preventDefault()

        if (!captchaVerified && is_active_google_captcha === "1") {
            setCaptchaError(translate("Please complete the captcha verification"))
            return
        }

        post(route("login.store"), {
            onSuccess: () => {}
        })
    }

    return (
        <AuthLayout>

			<h2 className="cs_login_title">{translate("Login with your account")}</h2>
			<p className="cs_login_subtitle">
				{translate("Sign in to access your account and keep your operations in sync.")}
			</p>
			{flash.success && (
				<div className="alert alert-success">
					<i className="fa-regular fa-circle-check"></i>
					<span>{translate(flash.success)}</span>
				</div>
			)}
			
			<div className="">
				{/* Email Field */}
				<div className="cs_mb_15">
					{/*<label htmlFor="login">{translate("Email")}*</label>*/}
					<div className="cs_input_shell">
						<span className="cs_input_addon">
							<i className="fa-regular fa-envelope"></i>
						</span>
						<input
							id="login"
							type="text"
							className="cs_form_field"
							placeholder={translate("Email")}
							onChange={(e) => setData("login", e.target.value)}
							value={data?.login}
						/>
					</div>
					{errors.login && (
					<span style={{ color: "red" }}>{errors.login}</span>
					)}
				</div>
				
				{/* Password Field */}
				<div className="cs_mb_15">
					{/*<label htmlFor="password">{translate("Password")}*</label>*/}
					<div className="cs_input_shell">
						<span className="cs_input_addon">
							<i className="fa-regular fa-lock"></i>
						</span>
						<input 
							id="password" 
							type="password" 
							className="cs_form_field" 
							placeholder={translate("Password")}
							onChange={(e) => setData("password", e.target.value)} 
						/>
						<button 
							type="button" 
							className="cs_toggle_password" 
							onClick={() => togglePasswordVisibility('password')}
							aria-label={translate("Toggle password visibility")}
						>
							<i className="fa-regular fa-eye m-1"></i>
							<span className="cs_toggle_text">{translate("Show")}</span>
						</button>
					</div>
					{errors.password && (
					<span style={{ color: "red" }}>{errors.password}</span>

					)}
				</div>
				
				{/* Remember Me & Forgot Password */}
				<div className="cs_card_row_1 cs_mb_15">
					<div>
						<div className="cs_custom_checkbox">
							<input
								className="cs_custom_checkbox_input"
								type="checkbox"
								id="gridCheck"
								checked={data.remember}
								onChange={(e) => setData("remember", e.target.checked)}
							/>
							<label htmlFor="gridCheck" className="cs_custom_checkbox_label">
								{translate("Remember me")}
							</label>
						</div>
					</div>
					<div>
						<Link href={route("forgot.password")} className="cs_card_text_btn">
							<i className="fa-regular fa-key m-1"></i>
							{translate("Forgot Password")}
						</Link>
					</div>
				</div>
				
				{/* CAPTCHA */}
				{is_active_google_captcha === "1" && (
					<div className="cs_mb_15">
						<ReCAPTCHA sitekey={captchaSiteKey} onChange={handleCaptchaChange} />
						{captchaError && (
							<div className="text-danger mb-3">
								<i className="fa-regular fa-circle-exclamation"></i>
								{captchaError}
							</div>
						)}
						{is_demo && (
							<div className="cs_demo_links">
								<LoginLink
									label={translate("Quick login as User")}
									redirectUrl={route("user.dashboard")}
									email="user@example.com"
									className="cs_btn cs_style_1 cs_type_2 cs_primary_bg cs_white_color cs_w_100_sm w-100"
								>
									<i className="fa-regular fa-user"></i>
								</LoginLink>
								<LoginLink
									label={translate("Quick login as Admin")}
									redirectUrl={route("admin.dashboard")}
									email="admin@example.com"
									className="cs_btn cs_style_1 cs_type_2 cs_primary_bg cs_white_color cs_w_100_sm w-100 mt-2"
								>
									<i className="fa-regular fa-user-tie"></i>
								</LoginLink>
							</div>
						)}
					</div>
				)}
				
				{/* Login Button */}
				<div className="cs_mb_20">
					<button
						type="submit"
						onClick={handleLogin}
						disabled={!captchaError && processing}
						className="cs_btn cs_style_1 cs_type_2 cs_primary_bg cs_white_color cs_w_100_sm w-100"
					>
						{translate("Login")}
					</button>
				</div>
				
				{/* Social Login */}
				{(is_facebook_login_active ?? is_google_login_active) && (
					<>
						<p className="cs_mb_20 cs_or_login">
							<span>{translate("Or login with")}</span>
						</p>
						
						<div className="cs_social_btns cs_mb_20">
							{is_facebook_login_active && (
								<a href={route("social.redirect", "facebook")} className="cs_social_btn cs_social_btn_facebook">
									<i className="fa-brands fa-facebook-f"></i>
									<span>{translate("Login With Facebook")}</span>
								</a>
							)}
							{is_google_login_active && (
								<a href={route("social.redirect", "google")} className="cs_social_btn cs_social_btn_google">
									<i className="fa-brands fa-google"></i>
									<span>{translate("Login With Google")}</span>
								</a>
							)}
						</div>
					</>
				)}
				
				{/* Register Link */}
			<p className="mb-0 cs_primary_color">
				{translate("Don't have an account")}{" "}
				<Link href={route("register")} className="cs_card_text_btn">
					{translate("Register Now")}
				</Link>
			</p>
			</div>

        </AuthLayout>
    )
}